import { useCallback, useRef, useState } from 'react'

export type FacingMode = "user" | "environment";

export type CameraError = {
  name: string;
  message: string;
  cause: unknown;
};

const toCameraError = (e: unknown): CameraError => ({
  name: (e as { name?: string })?.name ?? "CameraError",
  message: (e as { message?: string })?.message ?? String(e),
  cause: e,
});

const isFacingMode = (value: unknown): value is FacingMode =>
  value === "user" || value === "environment";

type UseCameraStreamOptions = {
  initialFacingMode?: FacingMode;
  videoConstraints?: MediaTrackConstraints;
  onStreamReady?: (track: MediaStreamTrack, stream: MediaStream) => void | Promise<void>;
};

const useCameraStream = ({
  initialFacingMode = "user",
  videoConstraints,
  onStreamReady,
}: UseCameraStreamOptions = {}) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [videoTrack, setVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [facingMode, setFacingMode] = useState<FacingMode>(initialFacingMode);
  const [error, setError] = useState<CameraError | null>(null);

  const optionsRef = useRef({ videoConstraints, onStreamReady });
  optionsRef.current = { videoConstraints, onStreamReady };

  const openStream = useCallback(async (mode: FacingMode): Promise<MediaStream | null> => {
    const options = optionsRef.current;
    setError(null);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { ...options.videoConstraints, facingMode: mode },
        audio: false,
      });

      const track = mediaStream.getVideoTracks()[0] ?? null;
      if (track) {
        await options.onStreamReady?.(track, mediaStream);
      }

      setStream(mediaStream);
      setVideoTrack(track);
      setFacingMode(mode);

      return mediaStream;
    } catch (e) {
      setError(toCameraError(e));
      return null;
    }
  }, []);

  const startCamera = useCallback((mode?: FacingMode) => (
    openStream(isFacingMode(mode) ? mode : facingMode)
  ), [openStream, facingMode]);

  const stopCamera = useCallback(() => {
    if(stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setVideoTrack(null);
    }
  }, [stream]);

  const restartCameraStream = useCallback((mode: FacingMode) => {
    stream?.getTracks().forEach(track => track.stop());
    return openStream(isFacingMode(mode) ? mode : facingMode);
  }, [stream, openStream, facingMode]);

  const switchCamera = useCallback(async () => {
    const newMode: FacingMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    return restartCameraStream(newMode);
  }, [facingMode, restartCameraStream]);

  return {
    stream,
    videoTrack,
    facingMode,
    error,
    startCamera,
    stopCamera,
    restartCameraStream,
    switchCamera,
  };
}

export default useCameraStream;
